import { useState, useEffect, useCallback } from "react";

// ── CONFIG ───────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://htpedzjlwfcglwelgofu.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh0cGVkempsd2ZjZ2x3ZWxnb2Z1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI2MjY1MzIsImV4cCI6MjA4ODIwMjUzMn0.1dqgDtrls3m7qAzzLqLqVpe6bNkvgViGRAIIcLnQb8M";
const EDGE_URL     = `${SUPABASE_URL}/functions/v1`;

const hdrs = {
  "apikey":        SUPABASE_KEY,
  "Authorization": `Bearer ${SUPABASE_KEY}`,
  "Content-Type":  "application/json",
};

const INDUSTRIES = [
  "Plumbing","Electrical","Cleaning","Landscaping","Painting",
  "Construction","HVAC","Pest Control","Security","Roofing","Tiling","Pool Services",
];

const PROVINCES = [
  "Western Cape","Gauteng","KwaZulu-Natal","Eastern Cape",
  "Limpopo","Mpumalanga","North West","Free State","Northern Cape",
];

const SA_CITIES = {
  "Western Cape":   ["Cape Town","Stellenbosch","Paarl","George","Knysna","Bellville","Somerset West"],
  "Gauteng":        ["Johannesburg","Pretoria","Sandton","Randburg","Centurion","Midrand","Soweto","Boksburg"],
  "KwaZulu-Natal":  ["Durban","Pietermaritzburg","Richards Bay","Umhlanga","Ballito","Newcastle"],
  "Eastern Cape":   ["Port Elizabeth","East London","Grahamstown","Mthatha"],
  "Limpopo":        ["Polokwane","Tzaneen","Mokopane"],
  "Mpumalanga":     ["Nelspruit","Witbank","Secunda"],
  "North West":     ["Rustenburg","Klerksdorp","Potchefstroom"],
  "Free State":     ["Bloemfontein","Welkom","Bethlehem"],
  "Northern Cape":  ["Kimberley","Upington","Springbok"],
};

const STATUS_COLORS = {
  not_sent:    "#64748b",
  queued:      "#f59e0b",
  sent:        "#3b82f6",
  bounced:     "#ef4444",
  replied:     "#10b981",
  unsubscribed:"#94a3b8",
};

const STATUS_LABELS = {
  not_sent:    "Not Sent",
  queued:      "Queued",
  sent:        "Sent",
  bounced:     "Bounced",
  replied:     "Replied ✓",
  unsubscribed:"Unsub'd",
};

// ── DB HELPERS ────────────────────────────────────────────────────────────────
const DB = {
  async leads(filters = {}) {
    let url = `${SUPABASE_URL}/rest/v1/fj_leads?order=created_at.desc&limit=200`;
    if (filters.industry)    url += `&industry=eq.${encodeURIComponent(filters.industry)}`;
    if (filters.province)    url += `&province=eq.${encodeURIComponent(filters.province)}`;
    if (filters.email_status)url += `&email_status=eq.${filters.email_status}`;
    if (filters.has_email)   url += `&email=not.is.null`;
    const r = await fetch(url, { headers: hdrs });
    return r.ok ? r.json() : [];
  },
  async stats() {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/fj_leads?select=email_status`, { headers: hdrs });
    const data = r.ok ? await r.json() : [];
    const counts = {};
    data.forEach((d) => { counts[d.email_status] = (counts[d.email_status] || 0) + 1; });
    return { total: data.length, ...counts };
  },
  async quota() {
    const today = new Date().toISOString().split("T")[0];
    const r = await fetch(`${SUPABASE_URL}/rest/v1/fj_send_quota?date=eq.${today}`, { headers: hdrs });
    const d = r.ok ? await r.json() : [];
    return d[0] || { sent_count: 0, daily_limit: 10 };
  },
  async scrapeJobs() {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/fj_scrape_jobs?order=created_at.desc&limit=10`, { headers: hdrs });
    return r.ok ? r.json() : [];
  },
  async updateLead(id, data) {
    await fetch(`${SUPABASE_URL}/rest/v1/fj_leads?id=eq.${id}`, {
      method: "PATCH", headers: hdrs, body: JSON.stringify(data),
    });
  },
  async deleteLead(id) {
    await fetch(`${SUPABASE_URL}/rest/v1/fj_leads?id=eq.${id}`, { method: "DELETE", headers: hdrs });
  },
  async emailLog(leadId) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/fj_email_log?lead_id=eq.${leadId}&order=sent_at.desc`, { headers: hdrs });
    return r.ok ? r.json() : [];
  },
  async setDailyLimit(limit) {
    const today = new Date().toISOString().split("T")[0];
    await fetch(`${SUPABASE_URL}/rest/v1/fj_send_quota`, {
      method: "POST", headers: { ...hdrs, "Prefer": "resolution=merge-duplicates" },
      body: JSON.stringify({ date: today, daily_limit: limit }),
    });
  },
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --ink:    #0a0f1a;
    --ink2:   #111827;
    --card:   #141c2b;
    --border: rgba(255,255,255,0.07);
    --gold:   #c8972a;
    --gold-l: #e4ae3a;
    --gold-p: rgba(200,151,42,0.1);
    --muted:  rgba(255,255,255,0.4);
    --text:   rgba(255,255,255,0.88);
    --green:  #10b981;
    --blue:   #3b82f6;
    --red:    #ef4444;
    --radius: 12px;
  }
  body { background: var(--ink); color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }
  h1,h2,h3,h4 { font-family: 'Syne', sans-serif; }

  .app { display: flex; min-height: 100vh; }
  .sidebar { width: 220px; background: var(--ink2); border-right: 1px solid var(--border); padding: 24px 0; display: flex; flex-direction: column; flex-shrink: 0; position: sticky; top: 0; height: 100vh; }
  .sidebar-logo { padding: 0 20px 24px; border-bottom: 1px solid var(--border); margin-bottom: 16px; }
  .sidebar-logo h2 { font-size: 17px; font-weight: 800; color: white; }
  .sidebar-logo span { font-size: 11px; color: var(--gold); letter-spacing: 1.5px; text-transform: uppercase; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 10px 20px; cursor: pointer; font-size: 13.5px; font-weight: 500; color: var(--muted); border-left: 3px solid transparent; transition: all 0.15s; }
  .nav-item:hover { color: var(--text); background: rgba(255,255,255,0.03); }
  .nav-item.active { color: var(--gold-l); border-left-color: var(--gold); background: var(--gold-p); }
  .nav-item svg { flex-shrink: 0; }

  .main { flex: 1; overflow-y: auto; }
  .page { padding: 32px; max-width: 1300px; }
  .page-header { margin-bottom: 28px; }
  .page-header h1 { font-size: 26px; font-weight: 800; color: white; }
  .page-header p  { font-size: 14px; color: var(--muted); margin-top: 4px; }

  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px,1fr)); gap: 14px; margin-bottom: 28px; }
  .stat-card { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px; }
  .stat-card .val { font-family: 'Syne',sans-serif; font-size: 28px; font-weight: 800; color: white; }
  .stat-card .lbl { font-size: 12px; color: var(--muted); margin-top: 2px; }
  .stat-card.gold  { border-color: rgba(200,151,42,0.3); }
  .stat-card.green { border-color: rgba(16,185,129,0.3); }
  .stat-card.blue  { border-color: rgba(59,130,246,0.3); }

  .card { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); }
  .card-header { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .card-header h3 { font-size: 15px; font-weight: 700; color: white; }

  .btn { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: 9px; font-size: 13.5px; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; font-family: inherit; }
  .btn-gold   { background: var(--gold); color: var(--ink); }
  .btn-gold:hover { background: var(--gold-l); }
  .btn-ghost  { background: rgba(255,255,255,0.06); color: var(--text); border: 1px solid var(--border); }
  .btn-ghost:hover { background: rgba(255,255,255,0.1); }
  .btn-red    { background: rgba(239,68,68,0.15); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
  .btn-red:hover { background: rgba(239,68,68,0.25); }
  .btn-green  { background: rgba(16,185,129,0.15); color: #34d399; border: 1px solid rgba(16,185,129,0.2); }
  .btn-green:hover { background: rgba(16,185,129,0.25); }
  .btn-sm     { padding: 6px 12px; font-size: 12px; }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .filter-row { display: flex; flex-wrap: wrap; gap: 10px; padding: 16px 20px; border-bottom: 1px solid var(--border); align-items: center; }
  select, input[type=text], input[type=email], input[type=number], textarea {
    background: rgba(255,255,255,0.05); border: 1px solid var(--border); border-radius: 8px;
    color: var(--text); padding: 8px 12px; font-size: 13px; font-family: inherit; outline: none;
  }
  select:focus, input:focus, textarea:focus { border-color: rgba(200,151,42,0.5); }
  select option { background: #1a2236; }
  label { font-size: 12px; color: var(--muted); margin-bottom: 4px; display: block; }

  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: 0.8px; text-transform: uppercase; border-bottom: 1px solid var(--border); white-space: nowrap; }
  td { padding: 11px 14px; border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: rgba(255,255,255,0.02); }

  .badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 700; }
  .chip  { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 8px; font-size: 12px; font-weight: 600; }

  .scrape-form { display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 12px; padding: 20px; align-items: end; }
  @media (max-width: 900px) { .scrape-form { grid-template-columns: 1fr 1fr; } }

  .progress-bar { height: 4px; background: rgba(255,255,255,0.08); border-radius: 2px; overflow: hidden; margin-top: 12px; }
  .progress-fill { height: 100%; background: var(--gold); border-radius: 2px; transition: width 0.4s; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .modal { background: var(--card); border: 1px solid var(--border); border-radius: 16px; width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; }
  .modal-header { padding: 20px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center; }
  .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 14px; }
  .modal-footer { padding: 16px 20px; border-top: 1px solid var(--border); display: flex; gap: 10px; justify-content: flex-end; }

  .profit-calc { max-width: 640px; }
  .calc-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .calc-result { background: linear-gradient(135deg, rgba(200,151,42,0.15), rgba(200,151,42,0.05)); border: 1px solid rgba(200,151,42,0.3); border-radius: 12px; padding: 20px; margin-top: 8px; }
  .calc-result .big { font-family: 'Syne',sans-serif; font-size: 36px; font-weight: 800; color: var(--gold-l); }
  .calc-result .sub { font-size: 13px; color: var(--muted); margin-top: 4px; }
  .calc-breakdown { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 14px; }
  .calc-breakdown .item { background: rgba(255,255,255,0.04); border-radius: 8px; padding: 10px 12px; }
  .calc-breakdown .item .n { font-size: 15px; font-weight: 700; color: white; }
  .calc-breakdown .item .l { font-size: 11px; color: var(--muted); }

  .empty { padding: 48px 24px; text-align: center; color: var(--muted); font-size: 14px; }
  .empty svg { opacity: 0.2; margin-bottom: 12px; }

  .tag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; background: rgba(255,255,255,0.06); color: var(--muted); }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spin { animation: spin 0.8s linear infinite; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  .fade-in { animation: fadeIn 0.25s ease; }

  .quota-bar { display: flex; align-items: center; gap: 12px; padding: 12px 20px; background: rgba(200,151,42,0.06); border-bottom: 1px solid rgba(200,151,42,0.15); }
  .quota-bar .label { font-size: 12.5px; font-weight: 600; color: var(--gold-l); }
  .quota-bar .value { font-size: 12px; color: var(--muted); }
`;

// ── ICONS ─────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16 }) => {
  const s = { width: size, height: size, stroke: "currentColor", fill: "none", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const icons = {
    leads:    <svg viewBox="0 0 24 24" {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    search:   <svg viewBox="0 0 24 24" {...s}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    email:    <svg viewBox="0 0 24 24" {...s}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    calc:     <svg viewBox="0 0 24 24" {...s}><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="16" y2="18"/></svg>,
    settings: <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    trash:    <svg viewBox="0 0 24 24" {...s}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    edit:     <svg viewBox="0 0 24 24" {...s}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    send:     <svg viewBox="0 0 24 24" {...s}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    refresh:  <svg viewBox="0 0 24 24" {...s}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
    check:    <svg viewBox="0 0 24 24" {...s}><polyline points="20 6 9 17 4 12"/></svg>,
    x:        <svg viewBox="0 0 24 24" {...s}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    map:      <svg viewBox="0 0 24 24" {...s}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    link:     <svg viewBox="0 0 24 24" {...s}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  };
  return icons[name] || <svg viewBox="0 0 24 24" {...s}/>;
};

// ── PROFIT CALCULATOR ────────────────────────────────────────────────────────
function ProfitCalculator() {
  const [v, setV] = useState({
    revenue: "", labour: "", materials: "", fuel: "", overhead: "", vat: "15",
  });
  const n = (x) => parseFloat(x) || 0;

  const revenue   = n(v.revenue);
  const costs     = n(v.labour) + n(v.materials) + n(v.fuel) + n(v.overhead);
  const vatAmount = revenue * (n(v.vat) / 100);
  const revenueExVat = revenue / (1 + n(v.vat) / 100);
  const profit    = revenueExVat - costs;
  const margin    = revenueExVat > 0 ? (profit / revenueExVat) * 100 : 0;
  const hourlyRate = n(v.labour) > 0 ? profit / (n(v.labour) / 150) : 0;

  const fmt = (x) => `R ${x.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div className="profit-calc fade-in">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "Syne", fontSize: 20, fontWeight: 800, color: "white", marginBottom: 4 }}>
          Free Profit Calculator
        </h2>
        <p style={{ fontSize: 13, color: "var(--muted)" }}>
          See your real profit margin after all costs. This is the tool you send to leads.
        </p>
      </div>

      <div className="calc-row" style={{ marginBottom: 14 }}>
        <div>
          <label>Total Invoice Amount (inc. VAT)</label>
          <input type="number" value={v.revenue} onChange={e => setV({ ...v, revenue: e.target.value })} placeholder="e.g. 5000" style={{ width: "100%" }} />
        </div>
        <div>
          <label>VAT Rate (%)</label>
          <select value={v.vat} onChange={e => setV({ ...v, vat: e.target.value })} style={{ width: "100%" }}>
            <option value="15">15% (Standard)</option>
            <option value="0">0% (Not VAT Registered)</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Your Costs</div>
      <div className="calc-row" style={{ marginBottom: 10 }}>
        <div>
          <label>Labour Cost (wages paid)</label>
          <input type="number" value={v.labour} onChange={e => setV({ ...v, labour: e.target.value })} placeholder="e.g. 800" style={{ width: "100%" }} />
        </div>
        <div>
          <label>Materials / Parts</label>
          <input type="number" value={v.materials} onChange={e => setV({ ...v, materials: e.target.value })} placeholder="e.g. 1200" style={{ width: "100%" }} />
        </div>
      </div>
      <div className="calc-row" style={{ marginBottom: 20 }}>
        <div>
          <label>Fuel / Travel</label>
          <input type="number" value={v.fuel} onChange={e => setV({ ...v, fuel: e.target.value })} placeholder="e.g. 150" style={{ width: "100%" }} />
        </div>
        <div>
          <label>Overhead (tools, insurance, etc.)</label>
          <input type="number" value={v.overhead} onChange={e => setV({ ...v, overhead: e.target.value })} placeholder="e.g. 200" style={{ width: "100%" }} />
        </div>
      </div>

      {revenue > 0 && (
        <div className="calc-result fade-in">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Your Net Profit</div>
              <div className="big" style={{ color: profit >= 0 ? "var(--gold-l)" : "#f87171" }}>{fmt(profit)}</div>
              <div className="sub">on a {fmt(revenue)} job</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Profit Margin</div>
              <div style={{ fontFamily: "Syne", fontSize: 32, fontWeight: 800, color: margin >= 20 ? "var(--green)" : margin >= 10 ? "var(--gold-l)" : "#f87171" }}>
                {margin.toFixed(1)}%
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>{margin >= 20 ? "✓ Healthy" : margin >= 10 ? "⚠ Thin" : "✗ Too low"}</div>
            </div>
          </div>

          <div className="calc-breakdown">
            <div className="item"><div className="n">{fmt(revenueExVat)}</div><div className="l">Revenue excl. VAT</div></div>
            <div className="item"><div className="n">{fmt(costs)}</div><div className="l">Total Costs</div></div>
            <div className="item"><div className="n">{fmt(vatAmount)}</div><div className="l">VAT to SARS</div></div>
            <div className="item"><div className="n">{fmt(hourlyRate)}</div><div className="l">Approx per hour</div></div>
          </div>

          {margin < 15 && (
            <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, fontSize: 12.5, color: "#fca5a5" }}>
              ⚠ Your margin is below 15%. Most SA contractors need 20–30% to stay profitable after tax and slow months.
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 20, padding: "14px 16px", background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid var(--border)", fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
        💡 <strong style={{ color: "white" }}>This is your lead magnet.</strong> When you email contractors, you link them to this exact calculator. It gives them real value and naturally introduces FieldJob as the tool that helps them stay profitable.
      </div>
    </div>
  );
}

// ── SCRAPE PAGE ───────────────────────────────────────────────────────────────
function ScrapePage({ onLeadsFound }) {
  const [industry, setIndustry] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [jobs, setJobs]         = useState([]);
  const [sources, setSources]   = useState(["google_maps","brabys","yellowsa","facebook"]);
  const toggleSource = (s) => setSources(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const cities = province ? (SA_CITIES[province] || []) : [];

  const loadJobs = useCallback(async () => {
    const data = await DB.scrapeJobs();
    setJobs(data);
  }, []);

  useEffect(() => { loadJobs(); }, []);

  const handleScrape = async () => {
    if (!industry) return;
    setLoading(true);
    setResult(null);

    // Create scrape job record
    const jobRes = await fetch(`${SUPABASE_URL}/rest/v1/fj_scrape_jobs`, {
      method: "POST",
      headers: { ...hdrs, "Prefer": "return=representation" },
      body: JSON.stringify({ industry, province, city, keyword: industry, status: "pending" }),
    });
    const [job] = await jobRes.json();

    try {
      const res = await fetch(`${EDGE_URL}/scrape-leads`, {
        method: "POST",
        headers: { ...hdrs },
        body: JSON.stringify({ industry, province, city, sources, job_id: job?.id }),
      });
      const data = await res.json();
      setResult(data);
      onLeadsFound();
      loadJobs();
    } catch (e) {
      setResult({ ok: false, error: e.message });
    }
    setLoading(false);
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <h1>Find New Leads</h1>
        <p>Search Google Maps and SA directories for contractor businesses</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h3>Search Parameters</h3></div>
        <div className="scrape-form">
          <div>
            <label>Industry *</label>
            <select value={industry} onChange={e => setIndustry(e.target.value)} style={{ width: "100%" }}>
              <option value="">Select industry…</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label>Province</label>
            <select value={province} onChange={e => { setProvince(e.target.value); setCity(""); }} style={{ width: "100%" }}>
              <option value="">All provinces</option>
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label>City</label>
            <select value={city} onChange={e => setCity(e.target.value)} style={{ width: "100%" }}>
              <option value="">All cities</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button className="btn btn-gold" onClick={handleScrape} disabled={!industry || loading}>
            {loading ? <span className="spin"><Icon name="refresh" size={14}/></span> : <Icon name="search" size={14}/>}
            {loading ? "Searching…" : "Find Leads"}
          </button>
        </div>

        {/* Source toggles */}
        <div style={{ padding: "0 20px 16px", display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--muted)", marginRight: 4, alignSelf: "center" }}>Sources:</span>
          {[
            { id: "google_maps", label: "Google Maps" },
            { id: "brabys",      label: "Brabys.co.za" },
            { id: "yellowsa",    label: "Yellow Pages SA" },
            { id: "facebook",    label: "Facebook Pages" },
          ].map(s => (
            <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: sources.includes(s.id) ? "var(--gold-l)" : "var(--muted)" }}>
              <input type="checkbox" checked={sources.includes(s.id)} onChange={() => toggleSource(s.id)} />
              {s.label}
            </label>
          ))}
          <span style={{ fontSize: 11, color: "var(--muted)", alignSelf: "center", marginLeft: 4 }}>
            {!sources.includes("facebook") ? "· Add FACEBOOK_ACCESS_TOKEN secret to enable Facebook" : ""}
          </span>
        </div>

        {loading && (
          <div style={{ padding: "0 20px 20px" }}>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>Searching Google Maps + extracting emails from websites…</div>
            <div className="progress-bar"><div className="progress-fill" style={{ width: "60%" }} /></div>
          </div>
        )}

        {result && (
          <div style={{ padding: "0 20px 20px" }}>
            <div style={{
              padding: "12px 16px", borderRadius: 10,
              background: result.ok ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
              border: `1px solid ${result.ok ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)"}`,
              fontSize: 13.5, color: result.ok ? "#34d399" : "#f87171",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <Icon name={result.ok ? "check" : "x"} size={16} />
              {result.ok
                ? `Found ${result.leads_found} leads total — ${Object.entries(result.by_source || {}).map(([k,v]) => `${v} from ${k.replace("_"," ")}`).join(", ")}. Go to the Leads tab.`
                : `Error: ${result.error || "Something went wrong. Check your Google Maps API key in Supabase secrets."}`}
            </div>
          </div>
        )}
      </div>

      {jobs.length > 0 && (
        <div className="card">
          <div className="card-header"><h3>Recent Searches</h3></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Industry</th><th>Location</th><th>Status</th><th>Leads Found</th><th>Date</th></tr></thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id}>
                    <td style={{ fontWeight: 600 }}>{j.industry}</td>
                    <td style={{ color: "var(--muted)" }}>{[j.city, j.province].filter(Boolean).join(", ") || "All SA"}</td>
                    <td>
                      <span className="badge" style={{
                        background: j.status === "done" ? "rgba(16,185,129,0.1)" : j.status === "running" ? "rgba(245,158,11,0.1)" : j.status === "failed" ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.06)",
                        color:      j.status === "done" ? "#34d399" : j.status === "running" ? "#fbbf24" : j.status === "failed" ? "#f87171" : "var(--muted)",
                      }}>{j.status}</span>
                    </td>
                    <td style={{ fontWeight: 700, color: "var(--gold-l)" }}>{j.leads_found || "—"}</td>
                    <td style={{ color: "var(--muted)", fontSize: 12 }}>{new Date(j.created_at).toLocaleDateString("en-ZA")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── LEADS TABLE PAGE ──────────────────────────────────────────────────────────
function LeadsPage() {
  const [leads, setLeads]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filters, setFilters]       = useState({ industry: "", province: "", email_status: "", has_email: false });
  const [selected, setSelected]     = useState([]);
  const [editLead, setEditLead]     = useState(null);
  const [viewLog, setViewLog]       = useState(null);
  const [emailLog, setEmailLog]     = useState([]);
  const [search, setSearch]         = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const data = await DB.leads(filters);
    setLeads(data);
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const filtered = leads.filter(l =>
    !search || l.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.city?.toLowerCase().includes(search.toLowerCase()) ||
    l.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelected(filtered.length === selected.length ? [] : filtered.map(l => l.id));
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await DB.deleteLead(id);
    load();
  };

  const handleBulkQueue = async () => {
    for (const id of selected) {
      await DB.updateLead(id, { email_status: "queued" });
    }
    setSelected([]);
    load();
  };

  const openLog = async (lead) => {
    setViewLog(lead);
    const log = await DB.emailLog(lead.id);
    setEmailLog(log);
  };

  return (
    <div className="page fade-in">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1>Leads Database</h1>
          <p>{leads.length} leads found</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {selected.length > 0 && (
            <button className="btn btn-gold" onClick={handleBulkQueue}>
              <Icon name="send" size={14}/> Queue {selected.length} for Email
            </button>
          )}
          <button className="btn btn-ghost" onClick={load}>
            <Icon name="refresh" size={14}/> Refresh
          </button>
        </div>
      </div>

      <div className="card">
        {/* Filters */}
        <div className="filter-row">
          <input
            type="text" placeholder="Search name, city, email…" value={search}
            onChange={e => setSearch(e.target.value)} style={{ width: 220 }}
          />
          <select value={filters.industry} onChange={e => setFilters({ ...filters, industry: e.target.value })}>
            <option value="">All industries</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select value={filters.province} onChange={e => setFilters({ ...filters, province: e.target.value })}>
            <option value="">All provinces</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filters.email_status} onChange={e => setFilters({ ...filters, email_status: e.target.value })}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, cursor: "pointer" }}>
            <input type="checkbox" checked={filters.has_email} onChange={e => setFilters({ ...filters, has_email: e.target.checked })} />
            <span style={{ fontSize: 13, color: "var(--muted)" }}>Has email only</span>
          </label>
        </div>

        {/* Table */}
        <div className="table-wrap">
          {loading ? (
            <div className="empty"><div className="spin" style={{ display:"inline-block" }}><Icon name="refresh" size={32}/></div><br/>Loading leads…</div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <Icon name="leads" size={40}/>
              <p>No leads yet. Use the <strong>Find Leads</strong> tab to search.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={selectAll}/></th>
                  <th>Business</th>
                  <th>Industry</th>
                  <th>Location</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Rating</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lead => (
                  <tr key={lead.id}>
                    <td><input type="checkbox" checked={selected.includes(lead.id)} onChange={() => toggleSelect(lead.id)}/></td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "white", marginBottom: 2 }}>{lead.business_name}</div>
                      {lead.website && (
                        <a href={lead.website} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--blue)", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                          <Icon name="link" size={10}/> website
                        </a>
                      )}
                    </td>
                    <td><span className="tag">{lead.industry}</span></td>
                    <td style={{ color: "var(--muted)", fontSize: 12 }}>{[lead.city, lead.province].filter(Boolean).join(", ")}</td>
                    <td style={{ fontSize: 12 }}>
                      {lead.email ? (
                        <span style={{ color: "var(--green)" }}>{lead.email}</span>
                      ) : (
                        <span style={{ color: "var(--muted)", fontSize: 11 }}>No email</span>
                      )}
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: 12 }}>{lead.phone || "—"}</td>
                    <td style={{ fontSize: 12 }}>
                      {lead.rating ? (
                        <span style={{ color: "var(--gold-l)", fontWeight: 600 }}>★ {lead.rating} <span style={{ color: "var(--muted)", fontWeight: 400 }}>({lead.review_count})</span></span>
                      ) : "—"}
                    </td>
                    <td>
                      <span className="badge" style={{
                        background: `${STATUS_COLORS[lead.email_status] || "#64748b"}22`,
                        color:       STATUS_COLORS[lead.email_status] || "#64748b",
                      }}>
                        {STATUS_LABELS[lead.email_status] || lead.email_status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        {lead.google_maps_url && (
                          <a href={lead.google_maps_url} target="_blank" rel="noreferrer">
                            <button className="btn btn-ghost btn-sm" title="View on Maps"><Icon name="map" size={12}/></button>
                          </a>
                        )}
                        <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => setEditLead(lead)}><Icon name="edit" size={12}/></button>
                        {lead.email_status === "sent" && (
                          <button className="btn btn-ghost btn-sm" title="Email log" onClick={() => openLog(lead)}><Icon name="email" size={12}/></button>
                        )}
                        <button className="btn btn-red btn-sm" title="Delete" onClick={() => handleDelete(lead.id)}><Icon name="trash" size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editLead && (
        <div className="modal-overlay" onClick={() => setEditLead(null)}>
          <div className="modal fade-in" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontFamily: "Syne", fontWeight: 700, color: "white" }}>Edit Lead</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditLead(null)}><Icon name="x" size={14}/></button>
            </div>
            <div className="modal-body">
              {[
                { key: "business_name", label: "Business Name" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "website", label: "Website" },
                { key: "city", label: "City" },
              ].map(f => (
                <div key={f.key}>
                  <label>{f.label}</label>
                  <input type="text" value={editLead[f.key] || ""} onChange={e => setEditLead({ ...editLead, [f.key]: e.target.value })} style={{ width: "100%" }} />
                </div>
              ))}
              <div>
                <label>Notes</label>
                <textarea value={editLead.notes || ""} onChange={e => setEditLead({ ...editLead, notes: e.target.value })} style={{ width: "100%", minHeight: 80, resize: "vertical" }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditLead(null)}>Cancel</button>
              <button className="btn btn-gold" onClick={async () => {
                await DB.updateLead(editLead.id, {
                  business_name: editLead.business_name, email: editLead.email,
                  phone: editLead.phone, website: editLead.website,
                  city: editLead.city, notes: editLead.notes,
                });
                setEditLead(null);
                load();
              }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Log Modal */}
      {viewLog && (
        <div className="modal-overlay" onClick={() => setViewLog(null)}>
          <div className="modal fade-in" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontFamily: "Syne", fontWeight: 700, color: "white" }}>Email Log — {viewLog.business_name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewLog(null)}><Icon name="x" size={14}/></button>
            </div>
            <div className="modal-body">
              {emailLog.length === 0 ? (
                <div style={{ color: "var(--muted)", fontSize: 13 }}>No emails logged yet.</div>
              ) : emailLog.map(e => (
                <div key={e.id} style={{ padding: "12px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 10, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: "white", marginBottom: 4 }}>{e.subject}</div>
                  <div style={{ color: "var(--muted)", fontSize: 12 }}>{new Date(e.sent_at).toLocaleString("en-ZA")} · {e.status}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EMAIL PAGE ────────────────────────────────────────────────────────────────
function EmailPage() {
  const [quota, setQuota]       = useState({ sent_count: 0, daily_limit: 10 });
  const [sending, setSending]   = useState(false);
  const [result, setResult]     = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [queued, setQueued]     = useState(0);

  const load = async () => {
    const q = await DB.quota();
    setQuota(q);
    const leads = await DB.leads({ email_status: "queued" });
    setQueued(leads.length);
  };

  useEffect(() => { load(); }, []);

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${EDGE_URL}/send-emails`, { method: "POST", headers: hdrs });
      const data = await res.json();
      setResult(data);
      load();
    } catch (e) {
      setResult({ ok: false, message: e.message });
    }
    setSending(false);
  };

  const pct = quota.daily_limit > 0 ? (quota.sent_count / quota.daily_limit) * 100 : 0;
  const remaining = Math.max(0, quota.daily_limit - quota.sent_count);

  return (
    <div className="page fade-in">
      <div className="page-header">
        <h1>Email Campaigns</h1>
        <p>Send cold outreach emails to queued leads — max {quota.daily_limit}/day</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        <div className="card">
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 16 }}>Today's Send Quota</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontFamily: "Syne", fontSize: 32, fontWeight: 800, color: "white" }}>{quota.sent_count}<span style={{ color: "var(--muted)", fontSize: 18 }}>/{quota.daily_limit}</span></div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "Syne", color: remaining > 0 ? "var(--green)" : "#f87171" }}>{remaining}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>remaining</div>
              </div>
            </div>
            <div className="progress-bar" style={{ height: 8, borderRadius: 4 }}>
              <div className="progress-fill" style={{ width: `${pct}%`, background: pct >= 100 ? "#ef4444" : "var(--gold)" }} />
            </div>
          </div>
        </div>

        <div className="card">
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 16 }}>Queue</div>
            <div style={{ fontFamily: "Syne", fontSize: 32, fontWeight: 800, color: "white", marginBottom: 8 }}>{queued}</div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>leads queued and ready to send</div>
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>Queue leads from the Leads tab → select → "Queue for Email"</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h3>Send Now</h3></div>
        <div style={{ padding: 20 }}>
          <p style={{ fontSize: 13.5, color: "var(--muted)", marginBottom: 20, lineHeight: 1.7 }}>
            This sends up to <strong style={{ color: "white" }}>{remaining} emails</strong> right now to your queued leads using your active email template. Each lead gets the profit calculator offer personalised with their business name and industry.
          </p>
          <button className="btn btn-gold" style={{ fontSize: 14, padding: "11px 24px" }} onClick={handleSend} disabled={sending || remaining === 0 || queued === 0}>
            {sending ? <><span className="spin"><Icon name="refresh" size={14}/></span> Sending…</> : <><Icon name="send" size={14}/> Send {Math.min(remaining, queued)} Emails Now</>}
          </button>
          {remaining === 0 && <div style={{ marginTop: 12, fontSize: 12.5, color: "#fbbf24" }}>Daily limit reached. Resets at midnight.</div>}
          {queued === 0 && remaining > 0 && <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--muted)" }}>No leads queued. Go to Leads → select leads → Queue for Email.</div>}

          {result && (
            <div style={{
              marginTop: 16, padding: "12px 16px", borderRadius: 10,
              background: result.ok ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
              border: `1px solid ${result.ok ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)"}`,
              fontSize: 13.5, color: result.ok ? "#34d399" : "#f87171",
            }}>
              {result.ok ? `✓ Sent ${result.sent} emails successfully` : `Error: ${result.message}`}
              {result.results?.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
                  {result.results.map((r, i) => (
                    <div key={i} style={{ marginTop: 4 }}>
                      {r.ok ? "✓" : "✗"} {r.lead} — {r.email || r.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Daily Limit Settings</h3></div>
        <div style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 14, lineHeight: 1.7 }}>
            Keep this at 5–10/day for cold outreach. Sending too many from a new domain will get you flagged as spam. Resend's free tier allows 100/day.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div>
              <label>New daily limit</label>
              <input type="number" value={newLimit} onChange={e => setNewLimit(e.target.value)} placeholder={String(quota.daily_limit)} style={{ width: 120 }} min="1" max="50"/>
            </div>
            <button className="btn btn-ghost" onClick={async () => {
              if (!newLimit) return;
              await DB.setDailyLimit(parseInt(newLimit));
              setNewLimit("");
              load();
            }}>Update Limit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS PAGE ─────────────────────────────────────────────────────────────
function SettingsPage() {
  return (
    <div className="page fade-in">
      <div className="page-header">
        <h1>Setup & Configuration</h1>
        <p>Required API keys and deployment steps</p>
      </div>
      {[
        {
          step: "1", title: "Run the SQL schema",
          content: "In your Supabase dashboard → SQL Editor, paste and run LEADFINDER_SCHEMA.sql. This creates all the required tables.",
          code: null,
        },
        {
          step: "2", title: "Add Supabase secrets",
          content: "In Supabase → Edge Functions → Secrets, add these environment variables:",
          code: `GOOGLE_MAPS_API_KEY=your_google_maps_key
RESEND_API_KEY=your_resend_key
FROM_EMAIL=arman@getdiswayne.com
FROM_NAME=Arman from FieldJob`,
        },
        {
          step: "3", title: "Get a Google Maps API key",
          content: "Go to console.cloud.google.com → Create project → Enable 'Places API' → Create API key. Free $200 credit/month = ~8,000 business searches.",
          code: null,
        },
        {
          step: "4", title: "Get a Resend API key",
          content: "Go to resend.com → Sign up free → Add domain getdiswayne.com → Create API key. Free tier: 100 emails/day, 3,000/month.",
          code: null,
        },
        {
          step: "5", title: "Deploy Edge Functions",
          content: "Install Supabase CLI, then run from your project folder:",
          code: `npx supabase functions deploy scrape-leads
npx supabase functions deploy send-emails`,
        },
        {
          step: "6", title: "Set up auto-send cron (optional)",
          content: "In Supabase → Database → Extensions → enable pg_cron. Then schedule daily sends at 9am SAST:",
          code: `select cron.schedule(
  'daily-email-send',
  '0 7 * * *',  -- 9am SAST = 7am UTC
  $$select net.http_post(url:='YOUR_EDGE_FUNCTION_URL/send-emails', headers:='{}', body:='{}')$$
);`,
        },
      ].map(s => (
        <div key={s.step} className="card" style={{ marginBottom: 16 }}>
          <div style={{ padding: 20 }}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: "var(--gold-p)", border: "1px solid rgba(200,151,42,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne", fontWeight: 800, color: "var(--gold-l)", fontSize: 14, flexShrink: 0 }}>
                {s.step}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "white", marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 13.5, color: "var(--muted)", lineHeight: 1.7, marginBottom: s.code ? 12 : 0 }}>{s.content}</div>
                {s.code && (
                  <pre style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", fontSize: 12, color: "#a5f3fc", overflowX: "auto", lineHeight: 1.8 }}>
                    {s.code}
                  </pre>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ onNav }) {
  const [stats, setStats] = useState({});
  const [quota, setQuota] = useState({ sent_count: 0, daily_limit: 10 });

  useEffect(() => {
    DB.stats().then(setStats);
    DB.quota().then(setQuota);
  }, []);

  const replyRate = stats.sent > 0 ? ((stats.replied || 0) / stats.sent * 100).toFixed(1) : "0";

  return (
    <div className="page fade-in">
      <div className="page-header">
        <h1>Lead Finder Dashboard</h1>
        <p>FieldJob cold outreach system for South African contractors</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="val">{stats.total || 0}</div><div className="lbl">Total Leads</div></div>
        <div className="stat-card gold"><div className="val" style={{ color: "var(--gold-l)" }}>{stats.not_sent || 0}</div><div className="lbl">Not Yet Contacted</div></div>
        <div className="stat-card blue"><div className="val" style={{ color: "#60a5fa" }}>{stats.sent || 0}</div><div className="lbl">Emails Sent</div></div>
        <div className="stat-card green"><div className="val" style={{ color: "var(--green)" }}>{stats.replied || 0}</div><div className="lbl">Replied</div></div>
        <div className="stat-card"><div className="val">{replyRate}%</div><div className="lbl">Reply Rate</div></div>
        <div className="stat-card"><div className="val">{quota.daily_limit - quota.sent_count}</div><div className="lbl">Emails Left Today</div></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontFamily: "Syne", fontWeight: 700, color: "white", marginBottom: 16, fontSize: 15 }}>Quick Actions</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button className="btn btn-gold" style={{ justifyContent: "flex-start" }} onClick={() => onNav("scrape")}>
              <Icon name="search" size={15}/> Find new leads in SA
            </button>
            <button className="btn btn-ghost" style={{ justifyContent: "flex-start" }} onClick={() => onNav("email")}>
              <Icon name="send" size={15}/> Send today's emails
            </button>
            <button className="btn btn-ghost" style={{ justifyContent: "flex-start" }} onClick={() => onNav("calc")}>
              <Icon name="calc" size={15}/> Preview profit calculator
            </button>
          </div>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontFamily: "Syne", fontWeight: 700, color: "white", marginBottom: 16, fontSize: 15 }}>How it works</h3>
          {[
            "Search Google Maps by industry + city",
            "System extracts emails from business websites",
            "Queue leads and send 5–10 personalised emails/day",
            "Each email offers the free Profit Calculator",
            "Replies convert to FieldJob demos",
          ].map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, background: "var(--gold-p)", border: "1px solid rgba(200,151,42,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "var(--gold-l)", flexShrink: 0 }}>{i + 1}</div>
              <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>{t}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontFamily: "Syne", fontWeight: 700, color: "white", marginBottom: 8, fontSize: 15 }}>⚠ Important — Before You Start</h3>
        <p style={{ fontSize: 13.5, color: "var(--muted)", lineHeight: 1.8 }}>
          1. Run <strong style={{ color: "white" }}>LEADFINDER_SCHEMA.sql</strong> in Supabase first. &nbsp;
          2. Add your <strong style={{ color: "white" }}>Google Maps API key</strong> and <strong style={{ color: "white" }}>Resend API key</strong> to Supabase Edge Function secrets. &nbsp;
          3. Deploy both edge functions. &nbsp;
          See the <span style={{ color: "var(--gold-l)", cursor: "pointer" }} onClick={() => onNav("settings")}>Setup Guide →</span>
        </p>
      </div>
    </div>
  );
}

// ── APP ROOT ──────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("dashboard");

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const nav = [
    { id: "dashboard", label: "Dashboard",        icon: "leads" },
    { id: "scrape",    label: "Find Leads",        icon: "search" },
    { id: "leads",     label: "Leads Database",    icon: "email" },
    { id: "email",     label: "Email Campaigns",   icon: "send" },
    { id: "calc",      label: "Profit Calculator", icon: "calc" },
    { id: "settings",  label: "Setup Guide",       icon: "settings" },
  ];

  const pages = {
    dashboard: <Dashboard onNav={setPage} />,
    scrape:    <ScrapePage onLeadsFound={() => {}} />,
    leads:     <LeadsPage />,
    email:     <EmailPage />,
    calc:      <div className="page fade-in"><ProfitCalculator /></div>,
    settings:  <SettingsPage />,
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h2>LeadFinder</h2>
          <span>by FieldJob</span>
        </div>
        {nav.map(n => (
          <div key={n.id} className={`nav-item ${page === n.id ? "active" : ""}`} onClick={() => setPage(n.id)}>
            <Icon name={n.icon} size={16}/> {n.label}
          </div>
        ))}
        <div style={{ marginTop: "auto", padding: "20px", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.7 }}>
            Targets SA contractors<br/>Google Maps · Resend · Supabase
          </div>
        </div>
      </aside>
      <div className="main">
        {pages[page] || pages.dashboard}
      </div>
    </div>
  );
}
